import { Button, Stack, Typography } from "@mui/material"

import { Calendar, dateFnsLocalizer } from "react-big-calendar"
import "../../../styles/event-calendar.css"

import format from "date-fns/format"
import parse from "date-fns/parse"
import startOfWeek from "date-fns/startOfWeek"
import endOfWeek from "date-fns/endOfWeek"
import addWeeks from "date-fns/addWeeks"
import getDay from "date-fns/getDay"
import { enAU } from "date-fns/locale"

import { CalendarSlot, MeetingStatus, ProposedTimeSlot } from "../../../types"

import React, { useState } from "react";
import TimeSelectModal from "./time-select-modal"
import { openForExistingSlot } from "../../../utils/schedule-utils"
import MeetingTimeslot from "./meeting-timeslot"
import { combineUserTimeSlots } from "../../../types-helpers"
import { useGetMeetingProposalData } from "../../../store"

const locales = {
    "en-AU": enAU
}

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
})

interface EventCalendarProps {
    userId: string
    meetingId: string
    joinCode: string
    meetingSlots: CalendarSlot[]
    proposedSlots: CalendarSlot[]
    meeting?: CalendarSlot | undefined
    onSetMeetingTime: (slot: ProposedTimeSlot) => void
    onAddTimeSlotDisplay: (slot: ProposedTimeSlot[]) => void
    onWeekChange?: (date: Date) => void
}

const getStartDate = (startTime: Date | undefined) => {
    const date = startTime ? new Date(startTime) : new Date()
    date.setHours(0, 0, 0)

    return date
}

const EventCalendar: React.FC<EventCalendarProps> = ({
    userId,
    meetingId,
    joinCode,
    proposedSlots,
    meetingSlots,
    meeting,
    onAddTimeSlotDisplay,
    onSetMeetingTime,
    onWeekChange,
}) => {
    const today = new Date()
    today.setHours(0, 0, 0)

    const minTime = new Date()
    minTime.setHours(6, 0, 0)

    const maxTime = new Date()
    maxTime.setHours(19, 0, 0)

    const [minDay] = useState<Date>(getStartDate(meeting?.startTime))

    const minNavigableWeekStart = startOfWeek(today, { weekStartsOn: 1 })

    const [date, setDate] = useState<Date>(minDay)
    const [selectedRange, setSelectedRange] = useState<CalendarSlot | null>(null)
    const [existingSlot, setExistingSlot] = useState<CalendarSlot | null>(null)

    // Single function to update + validate calendar date
    const updateCalendarView = (targetDate: Date) => {
        const targetWeekStart = startOfWeek(targetDate, { weekStartsOn: 1 })
        const validDate =
            targetWeekStart < minNavigableWeekStart
                ? minNavigableWeekStart
                : targetWeekStart

        setDate(validDate)

        if (onWeekChange) {
            onWeekChange(validDate)
        }
    }

    const weekStart = startOfWeek(date, { weekStartsOn: 1 })
    const weekEnd = endOfWeek(date, { weekStartsOn: 1 })

    const goPrevWeek = () => updateCalendarView(addWeeks(date, -1))
    const goNextWeek = () => updateCalendarView(addWeeks(date, 1))

    const { data: otherTimes = [] } = useGetMeetingProposalData(meetingId, userId, joinCode, weekStart, weekEnd)

    const openSlot = (
        toOpen: CalendarSlot,
        range: boolean,
    ) => {

        if (toOpen.meetingId !== meetingId) {
            return
        }

        if (toOpen.creatorId != userId) {
            setExistingSlot(toOpen)
            return
        }

        const {
            slot,
            conflicts,
            sameMeetingConflicts,
            sameValid,
        } = openForExistingSlot(
            toOpen,
            otherTimes,
            proposedSlots,
        )

        if (!slot) {
            return
        }

        if (sameMeetingConflicts.length > 0) {
            onAddTimeSlotDisplay(sameValid)
            return
        }

        if (conflicts.length > 0) {
            return
        }

        const combineSlots = combineUserTimeSlots([...proposedSlots, slot], userId)
        onAddTimeSlotDisplay(combineSlots)
    }

    const closeModal = () => {
        setSelectedRange(null)
    }

    const handleSetMeetingTime = (slot: ProposedTimeSlot) => {
        onSetMeetingTime(slot)
        closeModal()
    }

    return (
        <Stack
            direction="column"
            justifyContent="center"
            alignItems="center"
            spacing={2}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "98vw" }}
            >
                <Button
                    variant="outlined"
                    onClick={goPrevWeek}
                    disabled={weekStart <= minNavigableWeekStart}
                >
                    Previous week
                </Button>

                <Stack direction={"row"} alignItems={"center"} spacing={2}>
                    <Typography fontWeight={600}>
                        {format(weekStart, "dd MMM yyyy")} - {format(weekEnd, "dd MMM yyyy")}
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => updateCalendarView(today)}
                    >
                        Today
                    </Button>
                </Stack>

                <Button
                    variant="outlined"
                    onClick={goNextWeek}>
                    Next week
                </Button>
            </Stack>

            <Calendar
                min={minTime}
                max={maxTime}
                date={date}
                onNavigate={updateCalendarView}
                localizer={localizer}
                toolbar={false}
                events={[...proposedSlots, ...meetingSlots, ...otherTimes]}
                components={{
                    event: MeetingTimeslot,
                }}
                onSelectEvent={(event) => openSlot(event as CalendarSlot, false)}
                onSelectSlot={(slotInfo: any) => {
                    openSlot({
                        creatorId: userId,
                        meetingId,
                        meetingType: MeetingStatus.Proposed,
                        startTime: slotInfo.start as Date,
                        endTime: slotInfo.end as Date,
                        patientId: "",
                        attendeeIds: []
                    }, true)
                }}
                onView={() => { }}
                view={"week"}
                eventPropGetter={(event: CalendarSlot) => {
                    const borderColor = event.creatorId !== userId ? "black" : "white"

                    if (event.meetingId == meetingId) {
                        const backgroundColor = event.creatorId === userId ?
                            "#1f77b4" : "#4e79a7"

                        return {
                            style: {
                                backgroundColor,
                                borderColor,
                                color: "black",
                            },
                        }
                    }

                    if (!event.meetingType || event.meetingType == MeetingStatus.Proposed) {
                        return {
                            style: {
                                backgroundColor: "#ff7f0e",
                                borderColor,
                                color: "black",
                            },
                        }
                    }

                    if (event.meetingType === MeetingStatus.Scheduled) {
                        return {
                            style: {
                                backgroundColor: "#9467bd",
                                borderColor,
                                color: "black",
                            },
                        }
                    }

                    if (event.meetingType === MeetingStatus.Started) {
                        return {
                            style: {
                                backgroundColor: "#2ca02c",
                                borderColor,
                                color: "black",
                            },
                        }
                    }

                    return {
                        style: {
                            backgroundColor: "lightgray",
                            borderColor,
                            color: "black",
                        },
                    }
                }}
                selectable
                startAccessor="startTime"
                endAccessor="endTime"
                defaultView="week"
                views={["week"]}
                onShowMore={() => { }}
                style={{
                    width: "98vw",
                    overflow: "auto",
                    backgroundColor: "white",
                    borderRadius: ".8rem",
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
                    border: "1px solid #DDD",
                }}
                step={15}
            />

            {selectedRange && existingSlot &&
                <TimeSelectModal
                    open={selectedRange != null}
                    selectedRange={selectedRange}
                    existingSlot={existingSlot}
                    onClose={closeModal}
                    onSetMeetingTime={handleSetMeetingTime}
                />}
        </Stack >
    )
}

export default EventCalendar